import { Buffer } from 'buffer';
import React, { useEffect, useRef } from 'react';

import { IMGIX_URL } from '../src/env';
import { CLIPPER_DEFAULT_URL } from '../utils/constants';

/*
 * To get the best possible results, the contentImgixUrl prop expects an image
 * that has a 16:9 aspect ratio.
 */

interface ClipperProps {
  contentSelector: string;
  contentImgixUrl: string;
  logoUrl: string;
}

const Clipper = ({ contentSelector, contentImgixUrl, logoUrl }: ClipperProps) => {
  const minCharacters = 20;
  const maxCharacters = 720;
  const clipperRef = useRef(null);
  const contentElement: HTMLElement = document.querySelector(contentSelector);

  useEffect(() => {
    if (!contentElement) return;

    const listenerFunction = () => {
      clipperRef.current.style.visibility = 'hidden';

      const selection = document.getSelection();

      // Eject if selection starts (anchorNode) or ends (focusNode) outside the content
      if (
        !contentElement.contains(selection.anchorNode) ||
        !contentElement.contains(selection.focusNode)
      )
        return;

      const parsedSelection = selection.toString().trim();

      // Eject if too little or too much text is selected
      if (parsedSelection.length < minCharacters || parsedSelection.length > maxCharacters) return;

      const position = calculateTooltipPosition(selection);
      const imgixUrl = imgixTransformUrl(parsedSelection);

      clipperRef.current.style.visibility = 'visible';
      clipperRef.current.style.top = `${position.top}px`;
      clipperRef.current.style.left = `${position.left}px`;
      clipperRef.current.href = imgixUrl;
    };

    document.addEventListener('mouseup', listenerFunction);

    return () => {
      document.removeEventListener('mouseup', listenerFunction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentElement, clipperRef]);

  const calculateTooltipPosition = (selection) => {
    const selectionRect = selection.getRangeAt(0).getBoundingClientRect();
    const textRect = contentElement.getBoundingClientRect();
    const clipperRect = clipperRef.current.getBoundingClientRect();
    const lineHeightOffset = 12;

    const left = selectionRect.width / 2 - clipperRect.width / 2 + selectionRect.x;
    const top =
      selectionRect.top -
      textRect.y +
      contentElement.offsetTop -
      clipperRect.height -
      lineHeightOffset;

    return { top, left };
  };

  const imgixTransformUrl = (text) => {
    const url = new URL(contentImgixUrl.match(IMGIX_URL) ? contentImgixUrl : CLIPPER_DEFAULT_URL);

    const imgixTextOptions = new URLSearchParams();
    imgixTextOptions.append('w', '820');
    imgixTextOptions.append('bg', 'FFFFFF');
    imgixTextOptions.append('txt-font', 'Georgia');
    imgixTextOptions.append('txt-size', '32');
    imgixTextOptions.append('txt-color', '222426');
    imgixTextOptions.append('txt-lead', '6');
    imgixTextOptions.append('txt-pad', '20');
    imgixTextOptions.append('txt-fit', 'max');
    imgixTextOptions.append('txt', text);

    const textOverlayParam = `${IMGIX_URL}/~text?${imgixTextOptions.toString()}`;

    const imgixOptions = new URLSearchParams();
    imgixOptions.append('w', '1200');
    imgixOptions.append('h', '735');
    imgixOptions.append('frame', '1');
    imgixOptions.append('fm', 'jpg');
    imgixOptions.append('auto', 'compress');
    imgixOptions.append('q', '95');
    imgixOptions.append('dl', 'article-clip.jpg');
    imgixOptions.append('bg', '222426');
    imgixOptions.append('fit', 'crop');
    imgixOptions.append('pad-bottom', '60');
    imgixOptions.append('blend-mode', 'normal');
    imgixOptions.append('blend-alpha', '100');
    imgixOptions.append('blend-h', '520');
    imgixOptions.append('blend-align', 'bottom');
    imgixOptions.append('blend-pad', '100');
    imgixOptions.append(
      'blend64',
      Buffer.from(textOverlayParam.replace('-', '')).toString('base64')
    );
    imgixOptions.append('mark-pad', '20');
    imgixOptions.append('mark-w', '200');
    imgixOptions.append('mark-y', '687'); // Places watermark in the bottom padding
    imgixOptions.append('mark64', Buffer.from(logoUrl).toString('base64'));

    url.search = imgixOptions.toString();

    return url.href;
  };

  if (!contentElement) return null;

  return (
    <a className="clipper-tooltip" ref={clipperRef}>
      Download Clip
    </a>
  );
};

export default Clipper;
